import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { isEmpty, pickBy } from 'lodash';

import cn from 'classnames';
import { Button, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import profileStyles from '../Profile.module.scss';
import styles from './User.module.scss';

import {
  getUserAttendedEvents,
  getUserHostedEvents,
  getUserFriends
} from 'legacy/actions/userActions';
import { showModal } from 'legacy/actions/modalActions';
import {
  sendFriendRequest,
  cancelFriendRequest,
  removeFriend,
  acceptFriendRequest,
  declineFriendRequest,
  getSentFriendRequests,
  getReceivedFriendRequests
} from 'legacy/actions/friendActions';

import DefaultUserPhoto from '../../../util/DefaultUserPhoto/DefaultUserPhoto';
import BounceHelmet from '../../../util/Helmet/Helmet';
import { LoadingState } from 'legacy/reducers/types';
import { UserMe, UserLean, FriendStatus, Privacy } from 'legacy/user/types';

import EventContainer from './EventContainer';
import Friends from './People';
import Gallery from './Gallery';

import { sendMoneyFlow } from '../../../../util/modalFlows';
import ActionRequiredToolTip from './components/ActionRequiredToolTip';

// Use shared types and extend with component-specific fields
type User = UserMe & {
  friends?: any[]; // Component needs friends list
  attendedEvents?: any[];
  hostedEvents?: any[];
};

// Simple interface for friend requests - keep local since it's minimal
interface FriendRequest {
  _id: string;
  name: string;
}



interface UserProps {
  _id: string;
  self: User;
  user: User;
  onMount: string | null;
  receivedRequests: FriendRequest[];
  sentRequests: FriendRequest[];
  getUserHostedEvents: (id: string) => void;
  getUserAttendedEvents: (id: string) => void;
  getUserFriends: (id: string) => void;
  removeFriend: (userId: string) => void;
  sendFriendRequest: (user: User) => void;
  cancelFriendRequest: (user: User) => void;
  acceptFriendRequest: (user: User) => void;
  declineFriendRequest: (user: User) => void;
  getSentFriendRequests: () => void;
  getReceivedFriendRequests: () => void;
  router: any;
  friendLoading: LoadingState;
  showModal: (type: string, config: any) => void;
  ownFriends: any[];
  isModalOpen: boolean;
}

const User = ({
  _id,
  self = {} as User,
  user = {} as User,
  onMount,
  receivedRequests = [],
  sentRequests = [],
  getUserHostedEvents,
  getUserAttendedEvents,
  getUserFriends,
  removeFriend,
  sendFriendRequest,
  cancelFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  getSentFriendRequests,
  getReceivedFriendRequests,
  router,
  friendLoading = {} as LoadingState,
  showModal,
  ownFriends = [],
  isModalOpen
}: UserProps) => {
  const { t } = useTranslation(['users', 'common']);
  const isSelf = self._id === user._id;

  useEffect(() => {
    getSentFriendRequests();
    getReceivedFriendRequests();
    getUserAttendedEvents(_id);
    getUserHostedEvents(_id);
    getUserFriends(_id);
  }, [_id]);

  const { attendedEvents = [], hostedEvents = [] } = user;

  const { profile = {} as UserMe['profile'], friends = [], privacy = {} } = user;
  const {
    socialMedia = {},
    interests = [],
    profession,
    education = {},
    gallery = []
  } = profile || {};

  useEffect(() => {
    if (
      router.asPath.includes(_id) &&
      !isModalOpen &&
      onMount === 'showFriends' &&
      !isEmpty(friends) &&
      friends[0].name
    ) {
      showModal('VIEW_FRIENDS', { friends });
    }
  }, [onMount, friends]);
  // handle privacy for different users
  const isFriend =
    !isEmpty(ownFriends.find((friend) => friend._id === user._id)) ||
    user.friendStatus === 'friends';
  const isReceivedRequest = !isEmpty(
    receivedRequests.find((req) => req._id === user._id)
  );
  const isSentRequest = !isEmpty(
    sentRequests.find((req) => req._id === user._id)
  );
  const isPending = isReceivedRequest || isSentRequest;
  const canSeeEvents =
    isSelf ||
    privacy?.events === 'everyone' ||
    (privacy?.events === 'friends' && isFriend);
  const canSeeContact =
    isSelf ||
    privacy?.contact === 'everyone' ||
    (privacy?.contact === 'friends' && isFriend);
  const canSeeProfile =
    isSelf ||
    privacy?.profile === 'everyone' ||
    (privacy?.profile === 'friends' && isFriend);

  const friendButtonIcon = !isPending ? (
    <i className={`fas fa-${isFriend ? 'minus' : 'plus'}`} />
  ) : (
    <img
      className={styles.pendingRequestIcon}
      src="/img/request_pending.svg"
      alt=""
    />
  );

  let friendButtonText;
  if (isFriend) {
    friendButtonText = 'Remove Friend';
  } else if (isSentRequest) {
    friendButtonText = 'Pending';
  } else {
    friendButtonText = 'Add Friend';
  }



  const socialIconMap = {
    facebook: <img src="/img/icons/social/icon-fb.svg" alt="" />,
    instagram: <img src="/img/icons/social/icon-ins.svg" alt="" />,
    linkedin: <i className="fab fa-linkedin" />,
    snapchat: <i className="fab fa-snapchat" />,
    twitter: <i className="fab fa-twitter-square" />
  };

  const socialColourMap = {
    facebook: 'rgba(22, 108, 240, 0.1)',
    instagram: 'rgba(255, 92, 0, 0.1)',
    linkedin: 'rgba(22, 108, 240, 0.1)',
    twitter: 'rgba(58, 161, 236, 0.1)',
    snapchat: 'rgba(255, 165, 81, 0.1)'
  };

  const socialLinks = Object.entries(pickBy(socialMedia, ({ url }) => url)).map(
    ([media, mediaData], index) => {
      const { url } = mediaData as { url: string };
      return (
      <a
        key={index}
        style={{ backgroundColor: socialColourMap[media] || '#ddd' }}
        className={styles.socialLink}
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        {socialIconMap[media]}
      </a>
      );
    }
  );

  const interestsElement = canSeeProfile ? (
    <div className={styles.interests} data-cy="user-interests">
      {interests.map((interest, index) => (
        <div key={index} className={styles.interest}>
          {interest}
        </div>
      ))}
    </div>
  ) : (
    <div className={styles.private}>This user&apos;s interests are private</div>
  );

  const sendMoney = (receiver) =>
    sendMoneyFlow(() => showModal('SEND_MONEY', { receiver }));

  const requestMoney = (sender) => showModal('REQUEST_MONEY', { sender });



  const formatPhone = (num) => {
    if (num) {
      const formatted = `${num.slice(0, 1)} (${num.slice(1, 4)}) ${num.slice(
        4,
        7
      )} ${num.slice(7)}`;
      return `+${formatted}`;
    }

    return num;
  };

  const showCancelFriendRequestModal = () => {
    Modal.confirm({
      title: 'Cancel friend request',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to cancel your friend request to ${user.name}?`,
      centered: true,
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Close',
      onOk() {
        cancelFriendRequest(user);
      }
    });
  };

  const showRemoveFriendModal = () => {
    Modal.confirm({
      title: 'Remove friend',
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      content: `Are you sure you want to unfriend ${user.name}?`,
      centered: true,
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        removeFriend(user._id);
      }
    });
  };
  return (
    <div className={styles.user}>
      <BounceHelmet
        title={
          isSelf
            ? 'Your Profile'
            : user?.firstName || user?.name || 'Your Profile'
        }
        description="Your Bounce profile containing payment, event, ticket, and feed information"
      />
      <div className={cn('bn-container', styles.wrapper, styles.userInfo)}>
        <div className={styles.coverPhoto} />

        <div className={styles.userDetails}>
          <div
            className={cn(profileStyles.profilePicWrapper, styles.profilePic)}
          >
            {user.photo ? (
              <img src={user.photo.url} alt="" />
            ) : (
              <DefaultUserPhoto style={{ fontSize: '50px' }} />
            )}
          </div>
          <div className={styles.headerRow}>
            <div className={styles.infoColumn}>
              <div className={styles.fixedRow}>
                <span className={styles.userName}>{user.name}</span>
                {canSeeProfile && (
                  <div className={styles.socialIconsRow}>{socialLinks}</div>
                )}
              </div>
              {canSeeProfile && (
                <div className={styles.subtitleRow} data-cy="user-subtitle-row">
                  {profession && (
                    <span>
                      {profession}
                      {(education.school || education.program) && ','}
                    </span>
                  )}
                  {education.school && <span>{education.school}</span>}
                  {education.program && <span>{education.program}</span>}
                </div>
              )}
              {canSeeContact && user.phone && (
                <div className={styles.phone}>{formatPhone(user.phone)}</div>
              )}
            </div>
            <div className={styles.headerActions}>
              <div className={styles.fixedRow}>
                {isSelf ? (
                  <div className="flex flex-col gap-4 md:items-center items-end">
                    <ActionRequiredToolTip />
                    <div className="flex flex-col md:flex-row gap-2 items-center">
                      <Button
                        className={`${styles.actionButton} text-base font-light rounded-2xl border border-gray-400 p-4`}
                        icon={<i className="fas fa-pen" />}
                        onClick={() => router.push('/profile?view=settings')}
                      >
                        {t('users:profile.actions.editProfile')}
                      </Button>
                      <Button
                        className={`${styles.actionButton} text-base font-light rounded-2xl border border-gray-400 p-4`}
                        icon={<i className="fas fa-wallet" />}
                        onClick={() => router.push('/payments')}
                      >
                        {t('users:profile.actions.payments')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row gap-2 items-center">
                    {isReceivedRequest ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          loading={
                            (friendLoading.ACCEPT_FRIEND_REQUEST) || false
                          }
                          className={styles.actionButton}
                          onClick={() => acceptFriendRequest(user)}
                        >
                          Accept
                        </Button>
                        <Button
                          loading={
                            (friendLoading.DECLINE_FRIEND_REQUEST) || false
                          }
                          className={styles.actionButton}
                          onClick={() => declineFriendRequest(user)}
                        >
                          Decline
                        </Button>
                      </div>
                    ) : (
                      <Button
                        loading={
                          (friendLoading.REMOVE_FRIEND ||
                          friendLoading.SEND_FRIEND_REQUEST ||
                          friendLoading.ACCEPT_FRIEND_REQUEST ||
                          friendLoading.DECLINE_FRIEND_REQUEST) || false
                        }
                        className={styles.actionButton}
                        icon={friendButtonIcon}
                        onClick={() => {
                          if (isFriend) {
                            showRemoveFriendModal();
                          } else if (isPending) {
                            showCancelFriendRequestModal();
                          } else {
                            sendFriendRequest(user);
                          }
                        }}
                      >
                        {friendButtonText}
                      </Button>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        className={styles.actionButton}
                        icon={<i className="fas fa-wallet" />}
                        onClick={() => sendMoney(user)}
                      >
                        Send Money
                      </Button>
                      <Button
                        className={styles.actionButton}
                        icon={<i className="fas fa-wallet" />}
                        onClick={() => requestMoney(user)}
                      >
                        Request Money
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.mainContentGrid}>
        <div className={cn('bn-container', styles.wrapper, styles.events)}>
          <div className={styles.cardHeader}>{t('users:profile.sections.events')}</div>
          {canSeeEvents ? (
            <EventContainer
              hosted={hostedEvents}
              attended={attendedEvents}
              onEventDelete={() => getUserHostedEvents(_id)}
            />
          ) : (
            <div className={styles.private} data-cy="user-events-private">
              {t('users:profile.events.private')}
            </div>
          )}
        </div>
        <div
          className={cn(
            'bn-container',
            styles.wrapper,
            styles.galleryContainer
          )}
        >
          <div className={styles.cardHeader}>{t('users:profile.sections.gallery')}</div>
          {canSeeProfile ? (
            <Gallery gallery={gallery} />
          ) : (
            <div className={styles.private} data-cy="user-gallery-private">
              {t('users:profile.gallery.private')}
            </div>
          )}
        </div>
        <div className={cn('bn-container', styles.wrapper)}>
          <div className={styles.cardHeader}>{t('users:profile.sections.friends')}</div>
          {canSeeProfile ? (
            <Friends
              onClick={() => {
                showModal('VIEW_FRIENDS', { friends });
              }}
              people={friends}
              data-cy="user-friends"
            />
          ) : (
            <div className={styles.private} data-cy="user-friends-private">
              {t('users:profile.friends.private')}
            </div>
          )}
        </div>
        <div className={cn('bn-container', styles.wrapper)}>
          <div className={styles.cardHeader}>{t('users:profile.sections.interests')}</div>
          {interests.length > 0 ? (
            interestsElement
          ) : (
            <div className={styles.empty} data-cy="user-interests-private">
              {t('users:profile.interests.empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function mapStateToProps(state: any) {
  return {
    self: state.user.user,
    device: state.app.device,
    loading: state.user.loading,
    errors: state.user.errors,
    fbLoaded: state.user.fbLoaded,
    receivedRequests: state.friends.requests,
    sentRequests: state.friends.sent,
    friendLoading: state.friends.loading,
    ownFriends: state.friends.friends,
    isModalOpen: state.modal.isOpen
  };
}

const actions = {
  removeFriend,
  sendFriendRequest,
  cancelFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  getSentFriendRequests,
  getReceivedFriendRequests,
  getUserAttendedEvents,
  getUserHostedEvents,
  getUserFriends,
  showModal
};

export default withRouter(connect(mapStateToProps, actions)(User));
